// ============================================================
// FILE: VersionFooter.js
// LOCATION: C:\gardenmarket\web\src\components\VersionFooter.js
// ============================================================
// PURPOSE:
//   Small, unobtrusive version string at the bottom of the app.
//
//   Two versions matter and they can differ:
//     - the BUILD version of this JavaScript bundle, baked in at
//       compile time by deploy.sh via REACT_APP_VERSION
//     - the version the API reports from /api/health
//
//   They disagree when a browser is running a cached old bundle
//   after a deploy, which is exactly the "why is it still doing
//   the old thing" situation that is otherwise hard to diagnose
//   over the phone with a gardener.
//
//   Click the version string for full detail, including whether
//   the database has every migration this API build requires.
//
// NOTE: in local dev the bundle shows "dev" because
// REACT_APP_VERSION is only set by deploy.sh. That is deliberate
// — it distinguishes a release build from a working copy. The
// API version in the panel still shows the real git version.
// ============================================================

import { useState, useEffect } from 'react';
import client from '../api/client';

const BUILD_VERSION = process.env.REACT_APP_VERSION || 'dev';
const BUILD_COMMIT  = process.env.REACT_APP_COMMIT  || null;

export default function VersionFooter() {
    const [open,    setOpen]    = useState(false);
    const [health,  setHealth]  = useState(null);
    const [failed,  setFailed]  = useState(false);

    useEffect(() => {
        if (!open || health || failed) return;
        client.get('/health')
            .then(r => setHealth(r.data))
            .catch(() => setFailed(true));
    }, [open, health, failed]);

    // Bundle and API disagree — usually a stale cached bundle.
    // Never flagged in dev, where the bundle is always "dev".
    const stale = health && health.version && health.version !== BUILD_VERSION
                  && BUILD_VERSION !== 'dev';

    const schemaBad = health && health.schema && !health.schema.in_sync;

    return (
        <div style={styles.wrap}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={styles.trigger}
                title="Click for build details"
            >
                <span style={styles.underline}>GardenHand {BUILD_VERSION}</span>
                {(stale || schemaBad) && (
                    <span style={styles.warnDot} title="Click for details"> ●</span>
                )}
            </button>

            {open && (
                <div style={styles.panel}>
                    <Row label="App build" value={BUILD_VERSION} />
                    {BUILD_COMMIT && <Row label="Commit" value={BUILD_COMMIT} />}

                    {failed && <Row label="API" value="unreachable" bad />}

                    {health && (
                        <>
                            <Row label="API version" value={health.version || '—'} bad={stale} />
                            <Row label="Environment" value={health.env || '—'} />
                            {health.built_at && (
                                <Row label="Deployed"
                                     value={new Date(health.built_at).toLocaleString()} />
                            )}
                            {health.schema && (
                                <Row
                                    label="Schema"
                                    value={health.schema.in_sync
                                        ? `${health.schema.applied} applied — in sync`
                                        : `${health.schema.missing?.length || '?'} missing`}
                                    bad={schemaBad}
                                />
                            )}
                        </>
                    )}

                    {/* Only migrations the API REQUIRES and the database
                        lacks. A database with extra migrations is fine
                        and reports in sync. */}
                    {schemaBad && health.schema.missing?.length > 0 && (
                        <div style={styles.missingBox}>
                            <div style={styles.missingTitle}>Missing migrations</div>
                            {health.schema.missing.map(m => (
                                <div key={m} style={styles.missingItem}>{m}</div>
                            ))}
                        </div>
                    )}

                    {stale && (
                        <p style={styles.staleNote}>
                            You're running an older build than the server.
                            Reload the page (Ctrl+Shift+R) to update.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function Row({ label, value, bad }) {
    return (
        <div style={styles.row}>
            <span style={styles.rowLabel}>{label}</span>
            <span style={bad ? styles.rowValueBad : styles.rowValue}>{value}</span>
        </div>
    );
}

const styles = {
    wrap:         { textAlign:'center', padding:'20px 12px 28px', position:'relative' },
    trigger:      { background:'none', border:'none', color:'#bbb', fontSize:'0.72rem', cursor:'pointer', padding:'4px 8px', fontFamily:'inherit' },
    // Dotted underline so it reads as clickable — it is easy to
    // miss that the version string opens a detail panel.
    underline:    { borderBottom:'1px dotted #ccc', paddingBottom:'1px' },
    warnDot:      { color:'#e67e22', fontSize:'0.8rem' },
    panel:        { display:'inline-block', textAlign:'left', marginTop:'8px', padding:'12px 16px', background:'white', border:'1px solid #e5e5e5', borderRadius:'8px', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', fontSize:'0.75rem', minWidth:'270px', maxWidth:'340px' },
    row:          { display:'flex', justifyContent:'space-between', gap:'16px', padding:'3px 0' },
    rowLabel:     { color:'#888', flexShrink:0 },
    rowValue:     { color:'#333', fontFamily:'ui-monospace, Menlo, Consolas, monospace', textAlign:'right', wordBreak:'break-word' },
    rowValueBad:  { color:'#c0392b', fontFamily:'ui-monospace, Menlo, Consolas, monospace', fontWeight:600, textAlign:'right', wordBreak:'break-word' },
    missingBox:   { marginTop:'10px', paddingTop:'8px', borderTop:'1px solid #eee' },
    missingTitle: { color:'#c0392b', fontWeight:600, marginBottom:'4px' },
    missingItem:  { color:'#666', fontFamily:'ui-monospace, Menlo, Consolas, monospace', fontSize:'0.7rem', wordBreak:'break-all', padding:'1px 0' },
    staleNote:    { marginTop:'10px', marginBottom:0, color:'#e67e22', fontSize:'0.72rem', lineHeight:1.5 },
};
